import { Button, Flex, SegmentGroup, SegmentGroupValueChangeDetails, Text } from '@chakra-ui/react';
import { ContractPosition } from 'helpers/fakeData';
import { useHref } from 'hooks/useHref';
import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';
import { CreateIcon } from 'vectors';

type Segment = {
  key: ContractPosition;
  label: string;
};
const SEGMENTS: Segment[] = [
  { key: 'BUYER', label: 'Buy' },
  { key: 'SELLER', label: 'Sell' },
];

interface Props {
  supportedPositions?: ContractPosition[];
  commodityId?: string;
}
const EnterPosition = ({ supportedPositions = ['BUYER', 'SELLER'], commodityId }: Props) => {
  const [selectedSegment, setSelectedSegment] = useState<ContractPosition>(supportedPositions[0]);
  const router = useRouter();
  const { createHref } = useHref();

  const handleValueChange = useCallback((e: SegmentGroupValueChangeDetails) => {
    const value = e.value;
    if (value == null) {
      return;
    }
    setSelectedSegment(value as ContractPosition);
  }, []);

  const onClickActionButton = useCallback(() => {
    const signalType = selectedSegment === 'BUYER' ? 'demand' : 'supply';
    router.push(createHref(commodityId, signalType));
  }, [selectedSegment, commodityId, createHref, router]);

  const availableSegments = SEGMENTS.filter((segment) => supportedPositions.includes(segment.key));

  return (
    <Flex
      display="flex"
      flexDirection="column"
      bg="surface.muted"
      px={4}
      py={5}
      borderRadius="xl"
      gap={4}
    >
      <SegmentGroup.Root
        value={selectedSegment}
        onValueChange={handleValueChange}
        size="xs"
        alignSelf="center"
      >
        <SegmentGroup.Indicator bg="surface.accent" />
        {availableSegments.map((segment) => (
          <SegmentGroup.Item key={segment.key} value={segment.key} cursor="pointer">
            <SegmentGroup.ItemText>{segment.label}</SegmentGroup.ItemText>
            <SegmentGroup.ItemHiddenInput />
          </SegmentGroup.Item>
        ))}
      </SegmentGroup.Root>
      <Flex flexDirection="column" gap={4} borderRadius="xl" bg="surface.background" px={5} py={4}>
        <CreateIcon mx="auto" boxSize="100px" color="gray.400" />
        <Text fontSize="xs" textAlign="center" fontWeight="medium">
          {selectedSegment === 'BUYER'
            ? 'Create a demand contract where you specify quantity, target price, and settlement horizon'
            : 'Respond to a demand with a commitment to cover portions of the total requested quantity'}
        </Text>
        <Button size="xs" borderRadius="lg" onClick={onClickActionButton}>
          {selectedSegment === 'BUYER' ? 'Signal demand' : 'Supply'}
        </Button>
      </Flex>
    </Flex>
  );
};

export default EnterPosition;
